import { For, Show, createSignal } from "solid-js";

import { DraftEmailModal } from "~/components/DraftEmailModal";
import { useAccountContacts } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  accId: () => string;
  accountName: () => string;
  onClose: () => void;
};

type Stage = { kind: "picker" } | { kind: "drafting"; conId: string; contactLabel: string };

/**
 * Two-step dialog: pick a recipient contact from the account's linked
 * contacts, then hand off to the existing `<DraftEmailModal>` with
 * `contextAccountId` set so the prompt knows what account this email
 * is on behalf of.
 *
 * Primary contact rendered first + flagged. Empty state nudges the rep
 * to add a contact first.
 */
export function AccountDraftEmailDialog(props: Props) {
  const links = useAccountContacts(props.wsKey, props.accId);
  const [stage, setStage] = createSignal<Stage>({ kind: "picker" });

  const sortedLinks = () => {
    const items = links.data?.items ?? [];
    return [...items].sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return (a.contact_full_name ?? "").localeCompare(b.contact_full_name ?? "");
    });
  };

  return (
    <>
      <Show when={stage().kind === "picker"}>
        <dialog
          open
          class="modal modal-open"
          aria-modal="true"
          aria-labelledby="acc-draft-picker-title"
        >
          <div class="modal-box max-w-lg">
            <h3 id="acc-draft-picker-title" class="font-bold text-lg mb-2">
              ✨ Draft email on behalf of {props.accountName()}
            </h3>
            <p class="text-sm opacity-70 mb-3">Pick a recipient:</p>

            <Show when={links.isLoading}>
              <p class="text-sm opacity-60 py-4">Loading contacts…</p>
            </Show>

            <Show when={!links.isLoading && sortedLinks().length === 0}>
              <p class="text-sm opacity-60 py-4">
                No contacts linked to this account yet. Add one from the contact's detail page or
                via the picker on the account.
              </p>
            </Show>

            <ul class="divide-y divide-base-200 max-h-80 overflow-y-auto">
              <For each={sortedLinks()}>
                {(link) => (
                  <li>
                    <button
                      type="button"
                      class="w-full text-left px-3 py-2 hover:bg-base-200 flex items-center gap-2"
                      onClick={() =>
                        setStage({
                          kind: "drafting",
                          conId: link.contact_id,
                          contactLabel: link.contact_full_name,
                        })
                      }
                    >
                      <div class="flex-1">
                        <div class="font-medium">{link.contact_full_name}</div>
                        <div class="text-xs opacity-60">
                          <Show when={link.contact_title}>
                            <span>{link.contact_title}</span>
                          </Show>
                          <Show when={link.contact_title && link.contact_primary_email}>
                            <span class="opacity-50"> · </span>
                          </Show>
                          <Show when={link.contact_primary_email}>
                            <span>{link.contact_primary_email}</span>
                          </Show>
                        </div>
                      </div>
                      <Show when={link.is_primary}>
                        <span class="badge badge-primary badge-xs">primary</span>
                      </Show>
                    </button>
                  </li>
                )}
              </For>
            </ul>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={props.onClose}>
                Cancel
              </button>
            </div>
          </div>
        </dialog>
      </Show>

      <Show when={stage().kind === "drafting"}>
        {(_) => {
          const s = stage() as Extract<Stage, { kind: "drafting" }>;
          return (
            <DraftEmailModal
              wsKey={props.wsKey}
              conId={() => s.conId}
              contactLabel={() => s.contactLabel}
              contextAccountId={props.accId}
              onClose={props.onClose}
            />
          );
        }}
      </Show>
    </>
  );
}
